import { LabelValue, RadioListField as FieldDefinition } from '@bybeam/platform-types';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import {
  GenericFormData,
  fieldUpdate,
  updateRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import { FieldContext } from '@platform-ui/utilities/application/fields/index.js';
import { safeKey } from '@platform-ui/utilities/application/utils.js';
import cx from 'classnames';
import { useMemo } from 'react';
import { FieldProps } from './types.js';

const computeOptions = (options, placeholderData): LabelValue[] =>
  options.map(({ description, label, value }) => ({
    ...(description
      ? { description: <Markdown>{replacePlaceholders(description, placeholderData)}</Markdown> }
      : {}),
    label: <Markdown>{replacePlaceholders(label, placeholderData)}</Markdown>,
    value,
  }));

export default function RadioListField<FormData extends GenericFormData>({
  field: { key, copy, options },
  context,
  question,
  validation,
  answers,
  dispatch,
  errors,
  repeated,
  setValidationOverrides: _,
  ...rest
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const placeholderData = usePlaceholder();
  const computedOptions = useMemo(
    () => computeOptions(options, placeholderData),
    [options, placeholderData],
  );
  const labelId = `${safeKey(key)}-label`;
  const selectOneId = `${safeKey(key)}-select-one`;
  return (
    <div
      className={cx(
        'flex flex-col gap-4',
        context !== FieldContext.Panel && 'lg:self-center w-field lg:max-w-[20rem]',
      )}
    >
      {copy && (
        <Typography id={labelId} variant="largeBody" alignment="center">
          {copy}
        </Typography>
      )}
      <Typography
        id={selectOneId}
        {...(context === FieldContext.Panel
          ? { variant: 'body' }
          : { variant: 'largeBody', alignment: 'center' })}
      >
        Please select one option{validation?.required && '*'}:
      </Typography>
      <RadioGroup
        name={key}
        selectedValue={`${answers[key]}`}
        onChange={(val: string): void => {
          repeated
            ? dispatch(updateRepeated(repeated.parentKey, repeated.index, key, val))
            : dispatch(fieldUpdate(key, val as FormData[typeof key]));
        }}
        items={computedOptions}
        error={!!errors[key]}
        ariaTags={{
          label: copy
            ? labelId
            : ['form-page-title', question.copy.intro && 'question-intro']
                .filter(Boolean)
                .join(' '),
          description: 'selectOneId',
        }}
        helperText={errors[key]?.toString()}
        {...rest}
      />
    </div>
  );
}
